import { Component, computed, EventEmitter, inject, Output, signal } from '@angular/core';
import { IWizardStepComponent } from '@mwe/models';
import { MeterPointChangeDocumentResponse, MeterPointChangeSignalState, MeterPointChangeType } from '../meterpoint-change-request.types';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HeadlineService, UiComponentsModule } from '@mwe/ui/components';
import {
  checkPrevDataAvailable,
  EMPTY_VALUE,
  getZaehlpunktnummerShort,
  meterPointChangeOptions,
} from '../meter-point-change-request.utils';
import { InfoCardType } from '@shared-ikp/model/info-card-type.enum';
import { InfoCardContentFieldComponent } from '@shared-ikp/components/cards/info-card/components/info-card-content-field/info-card-content-field.component';
import { Router } from '@angular/router';
import {
  ROUTE_CURRENT_CONTRACT_PARTNER,
  ROUTE_METER_POINT_CHANGE_REQUEST,
  ROUTE_NEW_CONTRACT_PARTNER,
  ROUTE_PRODUCTS_AND_DATE,
} from '../../../app.constants';
import { InfoCardSectionComponent } from '@shared-ikp/components/cards/info-card/components/info-card-section/info-card-section.component';
import {
  alternativeInvoicingAddressToFields,
  currentEmptyContractPartnerToFields,
  currentOccupiedContractPartnerToFields,
  InfoCardField,
  newContractPartnerToFields,
  newEmptyContractPartnerToFields,
} from './summary.utils';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { MeterPointSelection } from '../../../model/product-selection.model';
import { MeterPointChangeService } from '../../../service/meter-point/meter-point-change.service';
import { finalize } from 'rxjs';
import { LegacyUmmeldungStatus } from '../../../model/meter-point.model';
import { VertragspartnerSelectionType } from '../04_new_contract_partner/new-contract-partner.utils';

@Component({
  selector: 'ikp-summary',
  templateUrl: './summary.component.html',
  standalone: true,
  providers: [HeadlineService],
  imports: [TranslatePipe, UiComponentsModule, InfoCardContentFieldComponent, InfoCardSectionComponent],
})
export class SummaryComponent implements IWizardStepComponent {
  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);
  private readonly router = inject(Router);
  private readonly meterPointChangeService = inject(MeterPointChangeService);
  translationService = inject(TranslateService);

  protected isToLeerstandChange = computed(() => this.wizardStore.state().stepChangeType === MeterPointChangeType.OCCUPIED_TO_EMPTY);
  protected neuerVertragspartnerType = computed(() => this.wizardStore.state().newContractPartner.vertragspartnerSelection);
  protected neuerVertragspartnerHasAltInvoicingAddress = computed(
    () => this.isToLeerstandChange() && !!this.wizardStore.state().newContractPartner?.alternativeInvoicingAddress,
  );

  protected isDownloadWarningShown = signal(false);
  protected isPDFDownloading = signal(false);
  protected isEditDisabled = computed(() => {
    const status = this.wizardStore.state().meterPoint?.legacyUmmeldungStatus;
    return this.isPDFDownloading() || status === LegacyUmmeldungStatus.WARTEN_AUF_UNTERSCHRIFT;
  });
  protected hasPrevStepData = computed(() => {
    const isMeterPointSpecified = this.wizardStore.state().meterPoint !== null;
    const { emptyPartner, formData } = this.wizardStore.state().newContractPartner;
    return !!(isMeterPointSpecified && (emptyPartner || formData?.privatePartner || formData?.organizationPartner));
  });
  protected subtitleBullets: string[] = this.translationService.instant(
    'meterPoint.meterPointChangeRequest.summary.contractualDeclaration.subtitle-bullets',
  );

  // Cannot use output signals because of base Interface using @Output
  // As soon as MWE is migrated to output signals this can be removed
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- we use the defined name from interface
  @Output() readonly onHidePrevButton = new EventEmitter<boolean>();

  meterPointAddress = computed(() => {
    return getAddressStreetAndPostCodeFormatted(this.wizardStore.state().meterPoint?.adresse);
  });

  changeTypeText = computed(
    () => meterPointChangeOptions.find(changeType => changeType.id === this.wizardStore.state().stepChangeType)?.text,
  );

  productFields = computed<InfoCardField[][]>(() => {
    const productAndDate = this.wizardStore.state().productAndDate;

    return productAndDate?.meterPoints.map(meterPoint => [
      {
        label: 'meterPoint.meterPointChangeRequest.summary.product.productType',
        value: meterPoint?.category || EMPTY_VALUE,
        contentDivId: 'meterpoint-change-summary-producttype',
      },
      {
        label: 'meterPoint.meterPointChangeRequest.summary.product.meterpoint',
        value: getZaehlpunktnummerShort(meterPoint?.meterPointNumber) || EMPTY_VALUE,
        contentDivId: 'meterpoint-change-summary-meterpoint',
      },
      {
        label: 'meterPoint.meterPointChangeRequest.summary.product.meternumber',
        value: meterPoint?.meterNumber || EMPTY_VALUE,
        contentDivId: 'meterpoint-change-summary-meternumber',
      },
      {
        label: 'meterPoint.meterPointChangeRequest.summary.product.meterreading',
        value: this.getMeterCountValueWithUnit(meterPoint),
        contentDivId: 'meterpoint-change-summary-meterreading',
      },
    ]);
  });

  currentPartnerFields = computed<InfoCardField[]>(() => {
    const state = this.wizardStore.state();
    if (state?.currentContractPartner?.emptyContractPartner) {
      return currentEmptyContractPartnerToFields(state.currentContractPartner?.emptyContractPartner);
    } else if (state?.currentContractPartner?.occupiedContractPartner) {
      return currentOccupiedContractPartnerToFields(state.currentContractPartner?.occupiedContractPartner);
    } else {
      return [];
    }
  });

  newPartnerFields = computed<InfoCardField[]>(() => {
    const state = this.wizardStore.state();
    if (state?.newContractPartner?.formData) {
      return newContractPartnerToFields(state.meterPoint, state.newContractPartner);
    }
    if (state?.newContractPartner?.emptyPartner) {
      return newEmptyContractPartnerToFields(state.newContractPartner.emptyPartner);
    }
    return [];
  });

  alternativeInvoicingFields = computed<InfoCardField[]>(() => {
    const state = this.wizardStore.state();
    if (!this.isToLeerstandChange() || !state?.newContractPartner?.alternativeInvoicingAddress) {
      return [];
    }
    return alternativeInvoicingAddressToFields(state.newContractPartner.alternativeInvoicingAddress, this.neuerVertragspartnerType());
  });

  hasAllNeededData(): boolean {
    checkPrevDataAvailable(this.hasPrevStepData(), this.router, [ROUTE_METER_POINT_CHANGE_REQUEST, ROUTE_NEW_CONTRACT_PARTNER]);
    return true;
  }

  submit(): Promise<void> {
    return Promise.resolve(undefined);
  }

  validate(): boolean {
    this.isDownloadWarningShown.set(!this.isEditDisabled());
    return this.isEditDisabled() && !this.isPDFDownloading();
  }

  private getMeterCountValueWithUnit(meterPoint: MeterPointSelection): string {
    if (meterPoint?.meterCount) {
      return `${meterPoint.meterCount} kWh`;
    }
    return EMPTY_VALUE;
  }

  public navigateToStepWithSubRoute(stepSubRoute: string) {
    this.router.navigate([ROUTE_METER_POINT_CHANGE_REQUEST, stepSubRoute]);
  }

  protected downloadPdf(): void {
    this.onHidePrevButton.emit(true);
    this.isPDFDownloading.set(true);

    this.meterPointChangeService
      .createDocument(this.wizardStore.state())
      .pipe(finalize(() => this.isPDFDownloading.set(false)))
      .subscribe({
        next: (blobRef: MeterPointChangeDocumentResponse) => {
          this.wizardStore.patchState({ meterPoint: blobRef.updatedMeterPoint });
          this.savePDFFile(blobRef);

          if (this.isEditDisabled()) {
            this.isDownloadWarningShown.set(false);
          }
        },
        error: () => {
          this.onHidePrevButton.emit(false);
          this.isPDFDownloading.set(false);
          this.meterPointChangeService.handleDownloadErrorPopup();
        },
      });
  }

  private savePDFFile(blobRef: MeterPointChangeDocumentResponse): void {
    const objectURL = URL.createObjectURL(blobRef.blob);

    const a = Object.assign(document.createElement('a'), {
      href: objectURL,
      download: blobRef.filename,
    });

    a.click();
    URL.revokeObjectURL(objectURL);
    a.remove();
  }

  protected readonly InfoCardType = InfoCardType;
  protected readonly ROUTE_PRODUCTS_AND_DATE = ROUTE_PRODUCTS_AND_DATE;
  protected readonly ROUTE_CURRENT_CONTRACT_PARTNER = ROUTE_CURRENT_CONTRACT_PARTNER;
  protected readonly ROUTE_NEW_CONTRACT_PARTNER = ROUTE_NEW_CONTRACT_PARTNER;
  protected readonly MeterPointChangeType = MeterPointChangeType;
  protected readonly VertragspartnerSelectionType = VertragspartnerSelectionType;
}
